<script>
import { onDestroy } from "svelte";
import {
    watermarkState,
    formatWatermarkDateTime,
    getWatermarkLines,
    isWatermarkVisible,
    WATERMARK_SOURCE_REMOTE,
} from "@app/store/watermarkStore.svelte.js";

/**
 * Visual watermark drawn over the media player and under the caption/subtitle
 * preview. Configured by team admins in Options, or by an IFrame host with the
 * setWatermark remote method.
 *
 * @property {string[]} [lines] Render these lines instead of the live state (used by the options preview).
 * @property {string} [alignment] Vertical alignment override.
 * @property {number} [textOpacity] Text opacity override (0-100).
 * @property {number} [shadowOpacity] Shadow opacity override (0-100).
 * @property {boolean} [force] Render even when the watermark is disabled.
 */
let {
    lines = null,
    alignment = null,
    textOpacity = null,
    shadowOpacity = null,
    force = false,
} = $props();

let overlayWidth = $state(640);
let now = $state(formatWatermarkDateTime());
let clock;

/* The live clock only ticks for locally resolved watermarks. A host that calls
   setWatermark supplies a fixed timestamp instead. */
let needsClock = $derived(
    lines === null &&
        watermarkState.enabled &&
        watermarkState.showDateTime &&
        watermarkState.source !== WATERMARK_SOURCE_REMOTE,
);

let visible = $derived(force || lines !== null || isWatermarkVisible());

let watermarkLines = $derived(lines !== null ? lines : getWatermarkLines(now));

let activeAlignment = $derived(alignment || watermarkState.alignment || "center");

let activeTextOpacity = $derived(
    textOpacity === null ? watermarkState.textOpacity : textOpacity,
);

let activeShadowOpacity = $derived(
    shadowOpacity === null ? watermarkState.shadowOpacity : shadowOpacity,
);

/* Scale with the player so the watermark stays legible in a small panel and
   does not overpower a full screen preview. */
let fontSize = $derived(Math.min(28, Math.max(9, Math.round(overlayWidth / 42))));

const alignmentMap = {
    top: "flex-start",
    center: "center",
    bottom: "flex-end",
};

function toAlpha(opacity) {
    const value = Number(opacity);
    return Number.isFinite(value) ? Math.min(100, Math.max(0, value)) / 100 : 1;
}

$effect(() => {
    if (needsClock) {
        if (!clock) {
            now = formatWatermarkDateTime();
            clock = setInterval(() => {
                now = formatWatermarkDateTime();
            }, 1000);
        }
    } else if (clock) {
        clearInterval(clock);
        clock = null;
    }
});

onDestroy(() => {
    if (clock) {
        clearInterval(clock);
        clock = null;
    }
});
</script>

<div
    id="WatermarkOverlay"
    aria-hidden="true"
    bind:clientWidth={overlayWidth}
    style="justify-content: {alignmentMap[activeAlignment] || 'center'};">
    {#if visible && watermarkLines.length > 0}
        <div
            class="watermark-text"
            style="
                font-size: {fontSize}px;
                color: rgba(255, 255, 255, {toAlpha(activeTextOpacity)});
                text-shadow: 0 {Math.max(1, Math.round(fontSize / 12))}px {Math.max(2, Math.round(fontSize / 6))}px rgba(0, 0, 0, {toAlpha(activeShadowOpacity)});">
            {#each watermarkLines as line, index (index)}
                <div class="watermark-line">{line}</div>
            {/each}
        </div>
    {/if}
</div>

<style>
#WatermarkOverlay {
    /* Sits below the caption/subtitle preview (z-index 9999) */
    z-index: 9998;
    position: absolute;
    pointer-events: none;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    padding: 2%;
}

.watermark-text {
    font-family:
        system-ui,
        -apple-system,
        "Segoe UI",
        Roboto,
        sans-serif;
    font-weight: 600;
    line-height: 1.25;
    text-align: center;
    max-width: 100%;
    user-select: none;
    overflow-wrap: anywhere;
}

.watermark-line {
    white-space: pre-wrap;
}
</style>
